// @flow
import 'whatwg-fetch';
import _ from 'lodash';

import { exists, getCookie } from 'app/common/CommonUtils';
import { addTruthyParamsToUrl } from 'app/common/utils/UrlUtils';
import fetchMiddleware, {
  notifyFetchMiddleware,
} from 'app/common/middlewares/fetchMiddleware';
import { createErrorType } from 'app/common/error';

// Catches a very specific Firefox error that occurs when a fetch
// request is interrupted while inflight. This error causes the global
// error modal to appear if a user is quickly navigating to a page
// that requires a refresh while there are inflight fetch requests.
export const isFirefoxFetchError = (error: Error) => {
  const result = Boolean(
    error &&
      error.name === 'TypeError' &&
      error.message === 'NetworkError when attempting to fetch resource.'
  );

  return result;
};

export const withCsrfHeader = (headers?: { [string]: string }) => {
  // Always read from cookies in case a new CSRF token is set
  headers = new Headers(headers || {});
  const token = getCookie('csrftoken');

  if (token) {
    headers.set('X-CSRFToken', token);
  }

  return headers;
};

export const NetworkError = createErrorType('NetworkError', {
  assign(error) {
    return {
      networkErrorMessage: error.message,
      shouldGlobalizeError:
        error.shouldGlobalizeError !== false && !isFirefoxFetchError(error),
    };
  },
  summary(error) {
    return (
      "We're having some trouble connecting. You might try reloading the page or checking your internet connection. " +
      error.networkErrorMessage
    );
  },
});

const fetchSuccess = function(response: Response & { responseText?: string }) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }
  return response.text().then((body: string) => {
    // Hack to make legacy z.useful.errors work for now.
    response.responseText = body;
    throw response;
  });
};

// If the client doesn't already have a CSRF token, then retrieve one
const retrieveCsrfToken = () => {
  return getCookie('csrftoken')
    ? (Promise.resolve(): Promise<void>)
    : fetch('/api/v3/csrf', { credentials: 'same-origin' })
        .then(fetchSuccess)
        .catch(error => {
          throw new NetworkError({
            ...error,
            message: 'Unable to retrieve CSRF token.',
          });
        });
};

/**
 * The CSRF token needs to be the first thing retrieved before any request can
 * take place. So we'll make sure this happens by caching this promise, all
 * requests should wait until that resolves before proceeding. CSRF token is
 * required for POST operations. POST is used by GraphQL for retreiving data.
 */
export const csrfTokenReady = retrieveCsrfToken();

const csrfSafeMethod = function(method) {
  if (typeof method !== 'string') {
    return false;
  }
  //these HTTP methods do not require CSRF protection
  return /^(GET|HEAD|OPTIONS|TRACE)$/.test(method);
};

/**
 * Retry trying to get `window.location` if it's not available immediately.
 * We do that because, weirdly, we're seeing some errors in Sentry where `window.location` is `null`.
 * After some initial investigation, it looks like Firefox has a bug where `window.location`
 * is not being properly set until a couple of seconds after the page is loaded.
 */
const waitForLocation = function(totalTimeWaited = 0) {
  if (document.location) {
    return Promise.resolve(document.location);
  }

  if (totalTimeWaited > 2000) {
    throw new Error("Couldn't find document.location after 2 seconds.");
  }

  return new Promise(resolve => {
    setTimeout(() => {
      waitForLocation(totalTimeWaited + 100).then(resolve);
    }, 100);
  });
};

/**
 * Test that a given url is a same-origin URL.
 * url could be relative or scheme relative or absolute host + port.
 */
const sameOrigin = function(url) {
  return waitForLocation().then(location => {
    const host = location.host;
    const protocol = location.protocol;
    const sr_origin = '//' + host;
    const origin = protocol + sr_origin;

    /* Allow absolute or scheme relative URLs to same origin
     * or any other URL that isn't scheme relative or absolute i.e relative.
     */
    return (
      url === origin ||
      url.slice(0, origin.length + 1) === origin + '/' ||
      (url === sr_origin ||
        url.slice(0, sr_origin.length + 1) === sr_origin + '/') ||
      !/^(\/\/|http:|https:).*/.test(url)
    );
  });
};

const wrappedFetch = function(url: string, info?: RequestOptions) {
  info = info ? _.extend({}, info) : {};
  info.headers = new Headers(info.headers || {});
  info.headers.set('X-Requested-With', 'XMLHttpRequest'); // help django's request.is_ajax()
  info.credentials = 'same-origin';

  const isCsrfSafeMethod = csrfSafeMethod(info.method);
  const preRequest = isCsrfSafeMethod ? Promise.resolve() : csrfTokenReady;

  return (preRequest
    .then(() => {
      if (isCsrfSafeMethod) {
        return false;
      }

      return sameOrigin(url);
    })
    .then(shouldSetCsrfHeader => {
      if (info && shouldSetCsrfHeader) {
        info.headers = withCsrfHeader(info.headers);
      }

      return fetch
        .call(null, url, info)
        .then(response => {
          notifyFetchMiddleware(response);
          return response;
        })
        .catch(error => {
          throw new NetworkError(error);
        });
    }): Promise<Response>);
};

wrappedFetch.success = fetchSuccess;

wrappedFetch.json = function(response) {
  return response
    .json()
    .then(data => {
      return data;
    })
    .catch(err => {
      // Empty content is expected with a 204 and is valid,
      // though parsing it as JSON would fail.
      if (response.status === 204) {
        return null;
      }
      // Throw the error if it was a problem parsing.
      if (response.status >= 200 && response.status < 300) {
        throw err;
      }
      // Otherwise, maybe this was an error status and JSON wasn't expected.
      throw response;
    });
};

wrappedFetch.url = function(url, params) {
  if (!params) {
    return url;
  }
  const queryString = Object.keys(params).reduce(
    (prevQueryString, paramKey) => {
      const value = params[paramKey];
      let nextQueryString = prevQueryString;
      if (exists(value)) {
        if (nextQueryString) {
          nextQueryString += '&';
        }
        nextQueryString += `${encodeURIComponent(
          paramKey
        )}=${encodeURIComponent(value)}`;
      }
      return nextQueryString;
    },
    ''
  );
  return url + (queryString ? '?' : '') + queryString;
};

export const fetchJson = (uri: string, info?: RequestOptions) => {
  info = info ? _.extend({}, info) : {};
  info.headers = new Headers(info.headers || {});
  (info: Object).headers.set('Content-Type', 'application/json');
  (info: Object).headers.set('Accept', 'application/json');

  return (wrappedFetch(uri, info)
    .then(wrappedFetch.success)
    .then(wrappedFetch.json): Promise<*>);
};

// `fetchJson` via `method`, passing along a stringified
// `body` if it exists.
const fetchJsonWithMethod = (method, url, body) =>
  fetchJson(url, {
    ...(method !== 'GET' ? { body: body && JSON.stringify(body) } : {}),
    method,
  });

export const deleteJson = _.partial(fetchJsonWithMethod, 'DELETE');
export const getJson = _.partial(fetchJsonWithMethod, 'GET');
export const patchJson = _.partial(fetchJsonWithMethod, 'PATCH');
export const postJson = _.partial(fetchJsonWithMethod, 'POST');
export const putJson = _.partial(fetchJsonWithMethod, 'PUT');

export const getJsonWithAccountId = (accountId: string, url: string) =>
  getJson(addTruthyParamsToUrl(url, { account_id: accountId }));

export const postJsonWithAccountId = (
  accountId: string,
  url: string,
  body: {} = {}
) => postJson(url, { ...body, account_id: accountId });

export const sendFormData = (
  url: string,
  formData: FormData,
  opts?: RequestOptions = {}
) => {
  opts.headers = new Headers(opts.headers || {});
  (opts: Object).headers.set('Accept', 'application/json');
  const options = {
    method: 'POST',
    ...opts,
    body: formData,
  };
  return (wrappedFetch(url, options)
    .then(wrappedFetch.success)
    .then(wrappedFetch.json): Promise<*>);
};

const isResponseOfType = (type, response) =>
  _.isFunction(_.get(response, ['headers', 'get'])) &&
  response.headers.get('content-type').indexOf(type) > -1;

export const isJsonResponse = _.partial(isResponseOfType, 'application/json');
export const isHtmlResponse = _.partial(isResponseOfType, 'text/html');

const doesResponseHaveStatus = (status, response) =>
  response && response.status === status;
export const isUnauthorizedErrorResponse = _.partial(
  doesResponseHaveStatus,
  401
);
export const isForbiddenErrorResponse = _.partial(doesResponseHaveStatus, 403);

export const isCsrfErrorResponse = (response: Response) =>
  isForbiddenErrorResponse(response) && !isJsonResponse(response);

export default wrappedFetch;
export { fetchMiddleware };
